/**
 * ============================================================================
 * [P6-03] TrackingFilterBar - 트래킹 포스트 필터 바
 *
 * 기능:
 * - 검색 입력
 * - 정렬 드롭다운 (optgroup 사용)
 * - 상태 필터
 * - 날짜 범위 (from ~ to + 초기화)
 * - 범위 필터 (접기/펼치기)
 * - CSV 내보내기 버튼
 * ============================================================================
 */

import { useState, useCallback } from 'react'
import type { TrackingFilterState, TrackingSortOption, TrackingStatusFilter } from '@/types'
import styles from './Tracking.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TrackingFilterBarProps {
  filter: TrackingFilterState
  onFilterChange: (filter: Partial<TrackingFilterState>) => void
  onResetFilter: () => void
  onExportCSV: () => void
  totalCount: number
  filteredCount: number
}

// ============================================================================
// 정렬 옵션 그룹
// ============================================================================
const SORT_OPTIONS: { group: string; options: { value: TrackingSortOption; label: string }[] }[] = [
  {
    group: '업데이트 날짜',
    options: [
      { value: 'updatedDesc', label: '최신순' },
      { value: 'updatedAsc', label: '오래된순' },
    ],
  },
  {
    group: '조회수',
    options: [
      { value: 'viewsDesc', label: '조회수 높은순' },
      { value: 'viewsAsc', label: '조회수 낮은순' },
    ],
  },
  {
    group: '좋아요',
    options: [
      { value: 'likesDesc', label: '좋아요 많은순' },
      { value: 'likesAsc', label: '좋아요 적은순' },
    ],
  },
  {
    group: '댓글',
    options: [
      { value: 'commentsDesc', label: '댓글 많은순' },
      { value: 'commentsAsc', label: '댓글 적은순' },
    ],
  },
  {
    group: '공유',
    options: [
      { value: 'sharesDesc', label: '공유 많은순' },
      { value: 'sharesAsc', label: '공유 적은순' },
    ],
  },
  {
    group: '팔로우',
    options: [
      { value: 'followsDesc', label: '팔로우 많은순' },
      { value: 'followsAsc', label: '팔로우 적은순' },
    ],
  },
  {
    group: '기록수',
    options: [
      { value: 'entriesDesc', label: '기록 많은순' },
      { value: 'entriesAsc', label: '기록 적은순' },
    ],
  },
  {
    group: '기타',
    options: [
      { value: 'favoritesFirst', label: '즐겨찾기 먼저' },
    ],
  },
]

// ============================================================================
// 상태 필터 옵션
// ============================================================================
const STATUS_OPTIONS: { value: TrackingStatusFilter; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'active', label: '활성' },
  { value: 'inactive', label: '비활성' },
  { value: 'hasData', label: '데이터 있음' },
  { value: 'noData', label: '데이터 없음' },
]

// ============================================================================
// [P6-03] TrackingFilterBar Component
// ============================================================================
export function TrackingFilterBar({
  filter,
  onFilterChange,
  onResetFilter,
  onExportCSV,
  totalCount,
  filteredCount,
}: TrackingFilterBarProps) {
  const [showRangeFilters, setShowRangeFilters] = useState(false)

  // ========================================================================
  // 핸들러
  // ========================================================================
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ search: e.target.value })
    },
    [onFilterChange]
  )

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({ sortBy: e.target.value as TrackingSortOption })
    },
    [onFilterChange]
  )

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({ status: e.target.value as TrackingStatusFilter })
    },
    [onFilterChange]
  )

  const handleDateFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ dateFrom: e.target.value || undefined })
    },
    [onFilterChange]
  )

  const handleDateToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ dateTo: e.target.value || undefined })
    },
    [onFilterChange]
  )

  const handleDateReset = useCallback(() => {
    onFilterChange({ dateFrom: undefined, dateTo: undefined })
  }, [onFilterChange])

  const handleRangeChange = useCallback(
    (field: keyof TrackingFilterState, value: string) => {
      const numValue = value === '' ? undefined : parseInt(value, 10)
      onFilterChange({ [field]: numValue })
    },
    [onFilterChange]
  )

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.filterBar}>
      {/* 상단: 검색, 정렬, 상태, 버튼 */}
      <div className={styles.filterRow}>
        {/* 검색 */}
        <div className={styles.filterGroup}>
          <input
            type="text"
            placeholder="검색..."
            value={filter.search}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>

        {/* 정렬 */}
        <div className={styles.filterGroup}>
          <select
            value={filter.sortBy}
            onChange={handleSortChange}
            className={styles.filterSelect}
          >
            {SORT_OPTIONS.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* 상태 */}
        <div className={styles.filterGroup}>
          <select
            value={filter.status}
            onChange={handleStatusChange}
            className={styles.filterSelect}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* 날짜 범위 */}
        <div className={styles.filterGroup}>
          <input
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={handleDateFromChange}
            className={styles.dateInput}
          />
          <span className={styles.dateSeparator}>~</span>
          <input
            type="date"
            value={filter.dateTo ?? ''}
            onChange={handleDateToChange}
            className={styles.dateInput}
          />
          {(filter.dateFrom || filter.dateTo) && (
            <button
              type="button"
              onClick={handleDateReset}
              className={styles.resetDateBtn}
              title="날짜 초기화"
            >
              X
            </button>
          )}
        </div>

        {/* 버튼들 */}
        <div className={styles.filterActions}>
          <button
            type="button"
            onClick={() => setShowRangeFilters(!showRangeFilters)}
            className={styles.toggleRangeBtn}
          >
            {showRangeFilters ? '범위 필터 접기' : '범위 필터'}
          </button>
          <button
            type="button"
            onClick={onResetFilter}
            className={styles.resetBtn}
          >
            초기화
          </button>
          <button
            type="button"
            onClick={onExportCSV}
            className={styles.exportBtn}
          >
            CSV 내보내기
          </button>
        </div>
      </div>

      {/* 하단: 범위 필터 (접기/펼치기) */}
      {showRangeFilters && (
        <div className={styles.rangeFilters}>
          {/* 조회수 */}
          <div className={styles.rangeGroup}>
            <label>조회수</label>
            <input
              type="number"
              placeholder="최소"
              value={filter.minViews ?? ''}
              onChange={(e) => handleRangeChange('minViews', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              placeholder="최대"
              value={filter.maxViews ?? ''}
              onChange={(e) => handleRangeChange('maxViews', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
          </div>

          {/* 좋아요 */}
          <div className={styles.rangeGroup}>
            <label>좋아요</label>
            <input
              type="number"
              placeholder="최소"
              value={filter.minLikes ?? ''}
              onChange={(e) => handleRangeChange('minLikes', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              placeholder="최대"
              value={filter.maxLikes ?? ''}
              onChange={(e) => handleRangeChange('maxLikes', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
          </div>

          {/* 댓글 */}
          <div className={styles.rangeGroup}>
            <label>댓글</label>
            <input
              type="number"
              placeholder="최소"
              value={filter.minComments ?? ''}
              onChange={(e) => handleRangeChange('minComments', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              placeholder="최대"
              value={filter.maxComments ?? ''}
              onChange={(e) => handleRangeChange('maxComments', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
          </div>

          {/* 공유 */}
          <div className={styles.rangeGroup}>
            <label>공유</label>
            <input
              type="number"
              placeholder="최소"
              value={filter.minShares ?? ''}
              onChange={(e) => handleRangeChange('minShares', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              placeholder="최대"
              value={filter.maxShares ?? ''}
              onChange={(e) => handleRangeChange('maxShares', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
          </div>

          {/* 팔로우 */}
          <div className={styles.rangeGroup}>
            <label>팔로우</label>
            <input
              type="number"
              placeholder="최소"
              value={filter.minFollows ?? ''}
              onChange={(e) => handleRangeChange('minFollows', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
            <span>~</span>
            <input
              type="number"
              placeholder="최대"
              value={filter.maxFollows ?? ''}
              onChange={(e) => handleRangeChange('maxFollows', e.target.value)}
              className={styles.rangeInput}
              min="0"
            />
          </div>
        </div>
      )}

      {/* 필터 결과 카운트 */}
      <div className={styles.filterCount}>
        {filteredCount === totalCount ? (
          <span>전체 {totalCount}개</span>
        ) : (
          <span>
            {filteredCount}개 / 전체 {totalCount}개
          </span>
        )}
      </div>
    </div>
  )
}

export default TrackingFilterBar
